import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiGroup, BiSend } from 'react-icons/bi';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router';
import z from 'zod';

import { Modal, SegmentedControl } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { ContactInput, Input, Select, TextArea } from '@/components/Inputs';
import { INVALID_CONTACT_MESSAGE } from '@/components/Inputs/contactMessages';
import inputStyles from '@/components/Inputs/inputs.module.scss';
import { useTemporaryMember } from '@/hooks/useTemporaryMember';
import { CREW_INVITES_KEY } from '@/pages/Crew/crewRoster';
import { type TemporaryHire, TemporaryHireWizard } from '@/pages/Crew/TemporaryHireWizard';
import { usePermissions as useViewerPermissions } from '@/permissions/usePermissions';
import { useAuth } from '@/store/auth/auth.store';
import { contractErrorMessage, temporaryHireAllowed } from '@/store/contracts/contracts.mappers';
import { seatAvailabilityWarning } from '@/store/invites/invites.mappers';
import { useSeatAvailabilityQuery } from '@/store/invites/invites.store';
import { ownershipErrorMessage } from '@/store/ownership/ownership.mappers';
import { ownershipInvitesKey } from '@/store/ownership/ownership.store';
import { getServiceRolePermissions } from '@/store/permissions/permissions.api';
import type { Vessel } from '@/store/vessels/vessel.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { isOwnerGroupRole, UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast.ts';

import s from './AddCrewModal.module.scss';

const schema = z.object({
  // `/user/invite` takes either: an email gets the link by mail, a phone by SMS.
  contact: z
    .string()
    .email(INVALID_CONTACT_MESSAGE)
    .or(z.string().refine((value) => isValidPhoneNumber(value), INVALID_CONTACT_MESSAGE)),
  roleName: z.string('Please enter a job title.'),
  name: z.string().min(1, 'Please enter a name.'),
  // Only the co-owner invite asks for it; the backend caps it at 500 characters.
  message: z.string().max(500, 'Please keep the message under 500 characters.').optional(),
});

type InviteCrewMember = z.infer<typeof schema>;

interface AddCrewModalProps {
  onClose(): void;
  /**
   * Fixes the invitation's role and drops the picker — and the catalogue link with it,
   * since neither has anything left to offer once the role is settled.
   *
   * Given, the modal invites one role and says so — the owner invite a captain sends from
   * the vessel's Ownership section, and the invite a role sends from Settings' Roles &
   * Permissions, where the role that is open is already the subject. Omitted, it is the crew
   * invite it has always been.
   *
   * A bare string rather than {@link UserRole}: service roles are per-service and an owner
   * adds their own from Settings, so the set on offer is wider than the enum's.
   */
  role?: UserRole | string;
  /**
   * The vessel to invite onto. Defaults to the active one, which is what every crew list
   * shows — but the vessel detail page's switcher navigates between vessels without changing
   * accounts, so a screen showing another one has to name it or the invitation lands on the
   * wrong boat.
   */
  vessel?: Pick<Vessel, 'id' | 'vessel_name' | 'timezone'>;
}

type HireKind = 'permanent' | 'temporary';

const HIRE_KINDS = [
  { label: 'Permanent', value: 'permanent' },
  { label: 'Temporary', value: 'temporary' },
] as const;

export const AddCrewModal = ({ onClose, role, vessel: target }: AddCrewModalProps) => {
  const navigate = useNavigate();
  const { inviteUser } = useAuth();
  const activeVessel = useVessels((state) => state.vessel);
  const vessel = target ?? activeVessel;
  const queryClient = useQueryClient();
  const temporaryMember = useTemporaryMember(vessel?.id);
  // A temporary member hires only temporary crew, so the permanent form is never theirs.
  const [kind, setKind] = useState<HireKind>(temporaryMember ? 'temporary' : 'permanent');
  // The catalogue is another way to find who to hire, so it belongs only to the wizard's first step.
  const [wizardStep, setWizardStep] = useState(0);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      contact: '',
      roleName: role,
    },
  });

  /**
   * The role is written the way the rest of the app writes it — `mate-deckhand` reads
   * "Mate deckhand" — and that one label names the modal, its opening line and the toasts.
   */
  const isCoOwner = role === UserRole.CO_OWNER;
  const roleLabel = isCoOwner
    ? 'Co-owner'
    : role
      ? role.charAt(0).toUpperCase() + role.slice(1).replace(/-/g, ' ')
      : '';
  const noun = roleLabel || 'Crew';

  const invite = async ({ contact, name, roleName, message }: InviteCrewMember) => {
    try {
      await inviteUser(
        contact,
        name,
        roleName,
        vessel!.id,
        'vessel',
        vessel!.vessel_name,
        roleName,
        undefined,
        isCoOwner ? message?.trim() : undefined,
      );
      // Two reads of the same fact, because two screens ask for it differently:
      // the invitation tiles under My Yachts read the invite service's own list,
      // and the crew roster's Invited cut reads people's `/invites/vessel`.
      // Marking only the first stale left an invitation just sent off the roster
      // until a reload. By prefix rather than by vessel id: the roster is only
      // ever the active vessel's, so there is one entry either way.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invites-list'] }),
        queryClient.invalidateQueries({ queryKey: [CREW_INVITES_KEY] }),
        queryClient.invalidateQueries({ queryKey: ownershipInvitesKey(vessel!.id) }),
      ]);
      toast.success(`${noun} invitation sent`);
      onClose();
    } catch (err: unknown) {
      console.error(err);
      // A co-owner invite fails on a rule more often than on the network — already aboard, or no
      // management seat left — and the rule is the useful thing to say.
      toast.error(
        ownershipErrorMessage(
          err,
          `Couldn’t send the ${noun.toLowerCase()} invitation. Please try again.`,
        ),
      );
    }
  };

  const hireTemporary = async (hire: TemporaryHire) => {
    try {
      await inviteUser(
        hire.contact,
        hire.name,
        hire.role,
        vessel!.id,
        'vessel',
        vessel!.vessel_name,
        hire.role,
        undefined,
        hire.message,
        { startAt: hire.startAt, endAt: hire.endAt, permissions: hire.permissions },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invites-list'] }),
        queryClient.invalidateQueries({ queryKey: [CREW_INVITES_KEY] }),
      ]);
      toast.success('Temporary crew invitation sent');
      onClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error(contractErrorMessage(err, "Couldn't send the temporary crew invitation"));
    }
  };

  const { data: serviceRoles = [] } = useQuery({
    queryKey: ['service-roles', vessel?.id],
    queryFn: () => getServiceRolePermissions(vessel!.id),
    // Nothing to pick from when the caller already named the role.
    enabled: !role && !!vessel?.id,
  });

  const myRole = useAuth(
    (state) => state.session?.services?.find((x) => x.service_id === vessel?.id)?.role ?? '',
  );
  const { can } = useViewerPermissions();
  // Only an ownerless vessel hands a captain TransferVessel, so the right marks the acting captain.
  const actingCaptain = myRole === UserRole.CAPTAIN && can('TransferVessel');

  // The owner and co-owners are invited from the vessel's Ownership section, never as a job title.
  // A permanent captain is the owner group's to invite, and the acting captain's on a vessel with
  // no owner; any other captain hires only a temporary one.
  const roleOptions = serviceRoles
    .filter(
      ({ role }) =>
        role !== UserRole.GUEST &&
        !isOwnerGroupRole(role) &&
        (role !== UserRole.CAPTAIN || isOwnerGroupRole(myRole) || actingCaptain),
    )
    .map(({ role }) => ({
      label: role.charAt(0).toUpperCase() + role.slice(1).replace(/-/g, ' '),
      value: role,
    }));

  const intro = (() => {
    if (isCoOwner) {
      return 'Invite someone to own this vessel with you. They get the same access as the owner, except deleting the vessel, and the plan stays with the owner.';
    }
    if (role === UserRole.OWNER) {
      return 'Please fill in the form to invite this vessel’s owner. They’ll join through the link in their invitation.';
    }
    if (role) {
      return `Please fill in the form to invite someone to the ${roleLabel.toLowerCase()} role. They’ll join through the link in their invitation.`;
    }
    return 'Please fill in the form to invite a crew member. They’ll join through the link in their invitation.';
  })();

  // Picked or fixed, the role decides which seat the invitee takes; the form says when none is free.
  const seatRole = useWatch({ control, name: 'roleName' });
  const { data: seat } = useSeatAvailabilityQuery(vessel?.id, seatRole);
  const seatWarning = seatAvailabilityWarning(seat, kind === 'temporary');

  // A fixed role skips the wizard's own picker, so it is held to the same rule here.
  const temporaryAllowed = !!vessel && (!role || temporaryHireAllowed(role, myRole));

  const kindSwitch = temporaryAllowed && !temporaryMember && (
    <SegmentedControl
      options={HIRE_KINDS}
      value={kind}
      onChange={(next) => {
        // The wizard remounts at its first step whenever it is switched back to.
        setWizardStep(0);
        setKind(next);
      }}
      as="radio"
      fullWidth
      aria-label="Kind of hire"
    />
  );

  const catalogueLink = !role && (
    <>
      <div className={s.or}>or</div>
      <ButtonOutlined onClick={() => navigate('/crew')}>
        <BiGroup size={20} /> Find a Crew Member in Catalogue
      </ButtonOutlined>
    </>
  );

  if (kind === 'temporary' && vessel) {
    return (
      <Modal open title={role ? `Invite ${roleLabel}` : 'Invite Crew Member'} onClose={onClose}>
        <section className={s.modalContent}>
          {kindSwitch}
          <TemporaryHireWizard
            vessel={vessel}
            mode="external"
            fixedRole={role}
            onSubmit={hireTemporary}
            onStepChange={setWizardStep}
          />
          {wizardStep === 0 && catalogueLink}
        </section>
      </Modal>
    );
  }

  return (
    <Modal open title={role ? `Invite ${roleLabel}` : 'Invite Crew Member'} onClose={onClose}>
      <section className={s.modalContent}>
        {kindSwitch}
        <p className={s.intro}>{intro}</p>
        {role && seatWarning && (
          <p className={inputStyles.Warning}>
            <AiOutlineWarning size={14} aria-hidden="true" />
            {seatWarning}
          </p>
        )}
        <form className={s.inviteForm} onSubmit={handleSubmit(invite)}>
          <Input
            label="Name"
            placeholder="Alex Morgan"
            error={errors.name?.message}
            {...register('name')}
            mandatory
          />
          <Controller
            name="contact"
            control={control}
            render={({ field }) => (
              <ContactInput
                label="Email or Mobile Number"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.contact?.message}
                mandatory
              />
            )}
          />
          {!role && (
            <Controller
              name="roleName"
              control={control}
              render={({ field }) => (
                <Select
                  label="Job Title"
                  placeholder="Select Job Title"
                  mandatory
                  options={roleOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.roleName?.message}
                  warning={seatWarning}
                />
              )}
            />
          )}
          {isCoOwner && (
            <TextArea
              label="Message"
              placeholder="Looking forward to having you aboard."
              rows={3}
              maxLength={500}
              error={errors.message?.message}
              {...register('message')}
            />
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send Invitation'} <BiSend color="#fff" size={20} />
          </Button>
        </form>
        {catalogueLink}
      </section>
    </Modal>
  );
};
